import { ImagePlus, X } from 'lucide-react';
import { useRef } from 'react';
import { cn } from '@/lib/utils';
import { MAX_IMAGES_PER_OWNER } from '@/features/catalog-image/types';

/** A photo chosen but not yet uploaded (no owner row exists yet). */
export type StagedImage = { file: File; url: string };

/**
 * Photo picker for a not-yet-created entity (e.g. a new service). Holds files
 * locally with object-URL previews; the parent uploads them once the entity
 * exists. Mirrors {@link ImageGalleryEditor}'s look so create/edit feel the same.
 * Controlled: the parent owns the list and is responsible for revoking URLs.
 */
export function StagedImageGallery({
  files,
  on_change,
  label = 'Photos',
  className,
}: {
  files: StagedImage[];
  on_change: (next: StagedImage[]) => void;
  label?: string;
  className?: string;
}) {
  const file_ref = useRef<HTMLInputElement>(null);
  const full = files.length >= MAX_IMAGES_PER_OWNER;

  function on_pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file || full) return;
    on_change([...files, { file, url: URL.createObjectURL(file) }]);
  }

  function remove(index: number) {
    const target = files[index];
    if (target) URL.revokeObjectURL(target.url);
    on_change(files.filter((_, i) => i !== index));
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)} data-testid="staged-gallery">
      <span className="text-sm font-semibold">
        {label}{' '}
        <span className="font-normal text-muted-foreground">
          ({files.length}/{MAX_IMAGES_PER_OWNER})
        </span>
      </span>
      <div className="flex flex-wrap gap-2">
        {files.map((img, i) => (
          <div
            key={img.url}
            className="group relative h-20 w-20 overflow-hidden rounded-input border-2 border-brand-outline-variant/20 bg-muted shadow-clay-sm"
            data-testid={`staged-thumb-${i}`}
          >
            <img src={img.url} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label="Remove photo"
              data-testid={`staged-remove-${i}`}
              className="absolute right-0.5 top-0.5 grid h-6 w-6 place-items-center rounded-full bg-foreground/70 text-white opacity-0 transition-opacity hover:bg-destructive group-hover:opacity-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {!full && (
          <button
            type="button"
            onClick={() => file_ref.current?.click()}
            data-testid="staged-add"
            className="grid h-20 w-20 place-items-center rounded-input border-2 border-dashed border-brand-outline-variant/40 bg-secondary/40 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
          >
            <ImagePlus className="h-5 w-5" />
          </button>
        )}
        <input
          ref={file_ref}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={on_pick}
          data-testid="staged-input"
        />
      </div>
      <p className="text-xs text-muted-foreground">Photos upload when you create the service.</p>
    </div>
  );
}
